import { useAppTheme } from "../../hooks/useAppTheme";
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { useTranslation } from "../../utils/useTranslation";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal as RNModal,
  Pressable,
  Platform,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { replaceDataValues, renameLabel } from "../../utils/common";
import { View } from "../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../components/shared/Spinner";
import FeatherIcon from "react-native-vector-icons/Feather";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useColorScheme from "../../hooks/useColorScheme";
import _ from "lodash";
import dayjs from "dayjs";
import {
  headers,
  mdHeaders,
  smHeaders,
  phoneHeaders,
} from "./utils/deliveryHeaders";
import {
  nestedHeaders as deliveryHeader,
  mdNestedHeaders as deliveryMdHeaders,
  smNestedHeaders as deliverySmHeaders,
} from "./utils/deliveryDetailsHeaders";
import { deliveryModel } from "../../models/reportsModel";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { sortColumns } from "../../utils/sortColumns";
import { UploadAttachmentDeliveryModal } from "./utils/UploadAttachmentDeliveryContent";
import { DeliveryStatusConfirmModal } from "./utils/DeliveryStatusConfirmModal";
import { DownloadPdfDeliveryModal } from "./utils/DownloadPdfDeliveryContent";
import DeliveryRecentUploadsModal from "./partials/DeliveryRecentUploadsModal";
import { OrderNumberSearchModal } from "./partials/OrderNumberSearchModal";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";
import {
  Provider as PaperProvider,
  DefaultTheme,
  DarkTheme,
  DataTable,
  ThemeProvider,
  useTheme as usePaperTheme,
} from "react-native-paper";
import { Ionicons } from "@expo/vector-icons";
import { default as Modal } from "../../components/custom/Modal";
import DeliveryDetailsScreen from "./DeliveryDetailsScreen";
import { getDeliveryReportV2 } from "../../redux/dispatcher/deliveryDispatcher";
import {
  updateReportV2DateRange,
  updateReportV2Page,
  updateReportV2PageSize,
  updateReportV2Sort,
  searchReportV2,
  updateReportV2States,
  updateReportV2Statuses,
  updateReportV2OnlyToday,
  updateReportV2FetchAll,
} from "../../redux/reducers/deliveryReducer";
import { ServerSidePagination } from "../../components/custom/DataTable/ServerSidePagination";

const groupDeliveryByDocNum = (
  rows: Array<deliveryModel>,
): Array<deliveryModel> => {
  const map = new Map<string | number, any>();
  rows.forEach((row: any) => {
    if (!map.has(row.DocNum)) {
      map.set(row.DocNum, { ...row, items: [] });
    }
    map.get(row.DocNum).items.push(row);
  });
  return Array.from(map.values());
};

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
  dateFormat: "DD/MM/YYYY",
});

// Default page size for the delivery report. Dispatched once on mount so
// Redux's pageSize (10 by default in the slice) matches what this screen
// has always shown (20 rows). If you'd rather the slice default to 20
// everywhere, change deliveryInitialState.pageSize instead and drop this.
const DEFAULT_PAGE_SIZE = 20;

export default function DeliverytScreen({ route, navigation }: any) {
  const { colors, isDark } = useAppTheme();
  const deliveryFilterStyles = createDeliveryFilterStyles(colors);

  const scrollRef = useRef<ScrollView>(null);
  const isFocused = useIsFocused();
  const { t } = useTranslation();
  const colorScheme = useColorScheme();
  const dispatch = useDispatch<any>();

  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "documentNo",
    "reference",
    "documentNo",
    "portalOrderNo",
    "DocNum",
  ];
  const dataToBeConverted: object = {
    /*{ "searchString" : "replaceValue" }*/
    Dispatched: "Shipped",
    "Not Confirmed": "Pending for Payment",
  };

  const { userInfo } = useSelector((state: any) => state.configurations);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [deliveryAttachmentModalVisible, setDeliveryAttachmentModalVisible] =
    useState<boolean>(false);
  const [deliveryStatusModal, setDeliveryStatusModal] =
    useState<boolean>(false);
  const [downloadPdfModalVisible, setDownloadPdfModalVisible] =
    useState<boolean>(false);
  const [recentUploadsVisible, setRecentUploadsVisible] =
    useState<boolean>(false);
  const [orderSearchModalVisible, setOrderSearchModalVisible] =
    useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [mobileDetailsModalVisible, setMobileDetailsModalVisible] =
    useState<boolean>(false);
  const [itemDetailsModalVisible, setItemDetailsModalVisible] =
    useState<boolean>(false);
  const [selectedItemDetail, setSelectedItemDetail] = useState<any>(null);

  const paperTheme = usePaperTheme();
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  // Local, presentation-only state: which quick-filter chip LABEL is
  // highlighted. This does not drive filtering itself — the actual filter
  // values (dateFrom/dateTo, states, statuses) live in Redux and are what
  // get sent to the API. We keep this locally because a date range like
  // "Next 7 days" doesn't roundtrip cleanly back from raw dateFrom/dateTo,
  // so there's nothing in Redux to reliably derive the active label from.
  const [selectedDateLabels, setSelectedDateLabels] = useState<string[]>([
    "Today",
  ]);
  const [dateRangePickerOpen, setDateRangePickerOpen] =
    useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({ startDate: undefined, endDate: undefined });

  const stateFilters = ["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS"];
  const dateFilters = [
    "All",
    "Last 7 days",
    "Yesterday",
    "Today",
    "Next date",
    "Next 7 days",
    "Date Range",
  ];

  const formatShortDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateRangeLabel = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      return `${formatShortDate(customDateRange.startDate)} to ${formatShortDate(customDateRange.endDate)}`;
    }
    return "Date Range";
  };
  const { userRoles } = useSelector((state: any) => state.configurations);

  const {
    deliveryList,
    isDeliveryListLoaded,
    loadingReportV2,
    totalRows,
    totalPages,
    page,
    pageSize,
    sort,
    search,
    salesName,
    onlyToday,
    fetchAll,
    dateFrom,
    dateTo,
    states,
    statuses,
  } = useSelector((state: any) => state.delivery);

  const [selected, setSelected] = useState<deliveryModel>(
    {} as unknown as deliveryModel,
  );

  const [selectedDeliveryItem, setSelectedDeliveryItem] = useState<any>(
    {} as unknown as any,
  );

  //Table & Nested Table Headers
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers,
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      deliveryHeader,
    );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [filterExpanded, setFilterExpanded] = useState<boolean>(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<
    "state" | "date" | "status" | null
  >(null);

  const [, setContentOffSet] = useState<number>(0);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  const handleDownloadPdf = (row: any) => {
    setSelectedDeliveryItem(row);
    setDownloadPdfModalVisible(true);
  };

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("delivery_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
    AsyncStorage.getItem("delivery_column_order").then((stored) => {
      if (stored) {
        try {
          setColumnOrder(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // Establish the page size this screen has always used (20). Runs once —
  // after this, page size changes (if you add a page-size picker later)
  // should go through dispatch(updateReportV2PageSize(...)) directly.
  useEffect(() => {
    dispatch(updateReportV2PageSize(DEFAULT_PAGE_SIZE));
  }, []);

  // All available column
  const getAllHeaders = () => {
    return isLargeDevice
      ? headers
      : isMediumDevice
        ? mdHeaders
        : isMobileDevice
          ? phoneHeaders
          : smHeaders;
  };

  const getOrderedKeys = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => {
      if (allKeys.includes(k)) ordered.push(k);
    });
    allKeys.forEach((k) => {
      if (!ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  };

  const getFilteredHeaders = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map(
        (key) =>
          [key, baseHeaders[key]] as [
            string,
            Record<string, string | number | boolean>,
          ],
      );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, h) => sum + (Number(h.width) || 0),
      0,
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, h]) => sum + (Number(h.width) || 0),
      0,
    );
    const scale =
      totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<
      string,
      Record<string, string | number | boolean>
    > = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  useEffect(() => {
    if (isSmallDevice || isMobileDevice) return;
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, columnOrder, isLargeDevice, isMediumDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("delivery_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const moveColumn = useCallback(
    (fromKey: string, toKey: string) => {
      const allHeaders = getAllHeaders();
      const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
      const fromIdx = currentKeys.indexOf(fromKey);
      const toIdx = currentKeys.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const newKeys = [...currentKeys];
      newKeys.splice(fromIdx, 1);
      newKeys.splice(toIdx, 0, fromKey);
      newKeys.push("");
      setColumnOrder(newKeys);
      AsyncStorage.setItem("delivery_column_order", JSON.stringify(newKeys));
    },
    [columnOrder],
  );

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("delivery_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({
    activeKey: null,
    overKey: null,
  });

  const startColumnDrag = useCallback(
    (key: string) => {
      if (Platform.OS !== "web" || typeof window === "undefined") return;
      dragRef.current.activeKey = key;
      setDragActiveKey(key);

      const onMouseMove = (e: MouseEvent) => {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const row =
          el && (el as HTMLElement).closest
            ? (el as HTMLElement).closest("[data-columnkey]")
            : null;
        const overKey = row ? row.getAttribute("data-columnkey") : null;
        if (overKey !== dragRef.current.overKey) {
          dragRef.current.overKey = overKey;
          setDragOverKey(
            overKey && overKey !== dragRef.current.activeKey ? overKey : null,
          );
        }
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        (document.body.style as any).userSelect = "";
        const from = dragRef.current.activeKey;
        const to = dragRef.current.overKey;
        if (from && to && from !== to) {
          moveColumn(from, to);
        }
        dragRef.current = { activeKey: null, overKey: null };
        setDragActiveKey(null);
        setDragOverKey(null);
      };

      document.body.style.cursor = "grabbing";
      (document.body.style as any).userSelect = "none";
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [moveColumn],
  );

  const getDateRangeFromFilter = (
    filter: string,
  ): { dateFrom: string | null; dateTo: string | null } => {
    const today = dayjs();

    switch (filter) {
      case "Yesterday":
        return {
          dateFrom: today.subtract(1, "day").format("YYYY-MM-DD"),
          dateTo: today.subtract(1, "day").format("YYYY-MM-DD"),
        };

      case "Today":
        return {
          dateFrom: today.format("YYYY-MM-DD"),
          dateTo: today.format("YYYY-MM-DD"),
        };

      case "Last 7 days":
        return {
          dateFrom: today.subtract(7, "day").format("YYYY-MM-DD"),
          dateTo: today.format("YYYY-MM-DD"),
        };

      case "Next date":
        return {
          dateFrom: today.add(1, "day").format("YYYY-MM-DD"),
          dateTo: today.add(1, "day").format("YYYY-MM-DD"),
        };

      case "Next 7 days":
        return {
          dateFrom: today.format("YYYY-MM-DD"),
          dateTo: today.add(7, "day").format("YYYY-MM-DD"),
        };

      case "Next 14 days":
        return {
          dateFrom: today.format("YYYY-MM-DD"),
          dateTo: today.add(14, "day").format("YYYY-MM-DD"),
        };

      case "Next 30 days":
        return {
          dateFrom: today.format("YYYY-MM-DD"),
          dateTo: today.add(30, "day").format("YYYY-MM-DD"),
        };

      default:
        return {
          dateFrom: null,
          dateTo: null,
        };
    }
  };

  // ---- Redux-driven filter handlers ----
  // Every filter change resets to page 1, since the previous page number
  // may no longer exist under the new filter.

  const handleDateFilterSelect = (category: string) => {
    if (category === "Date Range") {
      setDateRangePickerOpen(true);
      return;
    }
    if (category === "All") {
      // "All": bypass both the today-shortcut and any explicit date range —
      // fetchAll tells the API to ignore date filtering entirely.
      setSelectedDateLabels([]);
      dispatch(updateReportV2OnlyToday(false));
      dispatch(updateReportV2FetchAll(true));
      dispatch(updateReportV2DateRange({ dateFrom: null, dateTo: null }));
    } else if (category === "Today") {
      // "Today" uses the dedicated onlyToday flag rather than an explicit
      // date range — this is the screen's default state.
      setSelectedDateLabels([category]);
      dispatch(updateReportV2OnlyToday(true));
      dispatch(updateReportV2FetchAll(false));
      dispatch(updateReportV2DateRange({ dateFrom: null, dateTo: null }));
    } else {
      // Any other named filter (Yesterday, Last 7 days, Next date, Next 7
      // days, etc.): onlyToday and fetchAll both turn off, and the filter
      // is expressed purely as an explicit dateFrom/dateTo range.
      setSelectedDateLabels([category]);
      dispatch(updateReportV2OnlyToday(false));
      dispatch(updateReportV2FetchAll(false));
      const range = getDateRangeFromFilter(category);
      console.log(range, "range");
      dispatch(updateReportV2DateRange(range));
    }
  };

  const handleStateToggle = (category: string) => {
    if (category === "all") {
      dispatch(updateReportV2States([]));
    } else {
      const newStates = (states as string[]).includes(category)
        ? (states as string[]).filter((s) => s !== category)
        : [...(states as string[]), category];
      dispatch(updateReportV2States(newStates));
    }
    dispatch(updateReportV2Page(1));
  };

  const handleStatusToggle = (status: string) => {
    if (status === "All") {
      dispatch(updateReportV2Statuses([]));
    } else {
      const newStatuses = (statuses as string[]).includes(status)
        ? (statuses as string[]).filter((s) => s !== status)
        : [...(statuses as string[]), status];
      dispatch(updateReportV2Statuses(newStatuses));
    }
    dispatch(updateReportV2Page(1));
  };

  const onHandleSortColumn = (columnKey: string) => {
    // Reuse the existing sortColumns util purely for its header-icon side
    // effect (it calls setTableHeaders internally to flip the sort arrow).
    // The sorted array it returns is discarded — actual sorting now happens
    // server-side via the `sort` value dispatched below.
    sortColumns(deliveryList, columnKey, tableHeaders, setTableHeaders);

    const direction =
      sort?.key === columnKey && sort?.direction === "asc" ? "desc" : "asc";
    dispatch(updateReportV2Sort({ key: columnKey, direction }));
    dispatch(updateReportV2Page(1));
  };

  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  // Debounced so typing doesn't fire a request per keystroke. Created once
  // via useRef so the debounce timer isn't reset on every render.
  const debouncedSearchRef = useRef(
    _.debounce((searchText: string) => {
      const cleanText = searchText.replace(/"/g, "").trim();
      if (cleanText === searchRef.current) return;
      dispatch(searchReportV2(cleanText));
      dispatch(updateReportV2Page(1));
    }, 400),
  );

  const onHandleSearch = (searchText: string) => {
    debouncedSearchRef.current(searchText);
  };

  const onHandleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const onHandleRowClick = (row: deliveryModel) => { };

  const onHandleRemoveRow = (containerId: string) => { };

  useEffect(() => {
    setRefresh(true);
  }, [_isDisplayViewOn]);

  // Header/device-responsive layout — unrelated to data fetching, kept as
  // its own effect so column-visibility changes don't trigger API calls.
  useEffect(() => {
    const baseHeaders = isLargeDevice
      ? headers
      : isMediumDevice
        ? mdHeaders
        : isMobileDevice
          ? phoneHeaders
          : smHeaders;
    setTableHeaders(
      isSmallDevice || isMobileDevice
        ? baseHeaders
        : getFilteredHeaders(baseHeaders),
    );

    setNestedTableHeaders(
      isLargeDevice
        ? deliveryHeader
        : isMediumDevice
          ? deliveryMdHeaders
          : deliverySmHeaders,
    );
  }, [
    columnOrder,
    hiddenColumns,
    isLargeDevice,
    isMediumDevice,
    isSmallDevice,
    isMobileDevice,
  ]);

  // Single source of truth for fetching the delivery report: any filter,
  // sort, search, or pagination change dispatched to Redux lands here and
  // re-triggers the API call. No client-side filtering/sorting/searching
  // happens anywhere else in this screen anymore.
  useEffect(() => {
    if (!isFocused) return;

    dispatch(
      getDeliveryReportV2({
        page,
        pageSize,
        sort,
        search,
        states,
        statuses,
        salesName,
        onlyToday,
        fetchAll,
        startDate: dateFrom,
        endDate: dateTo,
        profile: userInfo.team,
        city: userInfo.timezone || userInfo.zone_info,
      }),
    );

    if (isRefresh) setRefresh(false);
  }, [
    isFocused,
    isRefresh,
    page,
    pageSize,
    sort,
    search,
    states,
    statuses,
    salesName,
    onlyToday,
    fetchAll,
    dateFrom,
    dateTo,
  ]);

  const navigateToReportDetails = (order: any, action: string) => {
    navigation.navigate("DeliveryDetailsScreen", { order });
  };

  // Mobile card view opens the item details as an in-place modal instead of
  // navigating to a separate screen, matching the rest of the mobile UI.
  const onHandleMobileItemDetails = (order: any) => {
    setSelectedItemDetail(order);
    setItemDetailsModalVisible(true);
  };

  // NOTE: this only reflects statuses present on the CURRENT page of
  // results, since filtering/pagination now happens server-side. If you
  // need the full set of possible statuses for the filter chips regardless
  // of what's currently loaded, that should come from a small dedicated
  // endpoint/thunk (e.g. GET /delivery/statuses) rather than being derived
  // from deliveryList here.
  const availableStatuses = Array.from(
    new Set((deliveryList || []).map((item: any) => item.OrderStatus)),
  ).filter(Boolean) as string[];

  const groupedDeliveryList = groupDeliveryByDocNum(deliveryList);

  const MobileDeliveryCard = ({ delivery }: { delivery: deliveryModel }) => {
    const itemCount = (delivery as any).items
      ? (delivery as any).items.length
      : 0;

    return (
      <View style={cardStyles.card}>
        {/* Header */}
        <RNView style={cardStyles.cardHeader}>
          <RNView style={cardStyles.cardHeaderLeft}>
            <RNView style={cardStyles.containerIconWrap}>
              <Ionicons
                name="document-text-outline"
                size={18}
                color={isDark ? colors.primary : "#3B82F6"}
              />
            </RNView>
            <RNView style={{ flex: 1, backgroundColor: "transparent" }}>
              <Text style={cardStyles.containerNumber}>
                {delivery.CustomerName || delivery.DocNum}
              </Text>
            </RNView>
          </RNView>
          {/* Items Badge */}
          <TouchableOpacity
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              backgroundColor: isDark ? colors.border : "#E2E8F0",
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
            }}
            onPress={() => {
              setSelectedDeliveryItem(delivery);
              setMobileDetailsModalVisible(true);
            }}
          >
            <Text
              style={{
                fontSize: 10,
                fontWeight: "700",
                color: isDark ? "#E2E8F0" : "#475569",
              }}
            >
              {itemCount} {itemCount === 1 ? "ITEM" : "ITEMS"}
            </Text>
            <Ionicons
              name="chevron-forward"
              size={10}
              color={isDark ? "#E2E8F0" : "#475569"}
            />
          </TouchableOpacity>
        </RNView>

        <View style={cardStyles.divider} />

        {/* Timeline style */}
        <View style={cardStyles.dateRow}>
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>DOC. NO</Text>
            <Text style={cardStyles.dateValue}>{delivery.DocNum || "-"}</Text>
          </View>
          <View
            style={{
              width: 1,
              height: 24,
              backgroundColor: isDark ? colors.border : "#E2E8F0",
            }}
          />
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>
              {t("status") ? t("status").toUpperCase() : "STATUS"}
            </Text>
            <Text style={cardStyles.dateValue}>
              {renameLabel(
                (dataToBeConverted as any)[delivery.OrderStatus] ||
                delivery.OrderStatus,
              ) || "-"}
            </Text>
          </View>
          <View
            style={{
              width: 1,
              height: 24,
              backgroundColor: isDark ? colors.border : "#E2E8F0",
            }}
          />
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>
              {t("requestDate")
                ? t("requestDate").toUpperCase()
                : "REQUEST DATE"}
            </Text>
            <Text style={cardStyles.dateValue}>
              {delivery.RequestDate
                ? formatShortDate(new Date(delivery.RequestDate))
                : "-"}
            </Text>
          </View>
        </View>

        <View style={cardStyles.divider} />

        {/* Footer info */}
        <View style={[cardStyles.cardFooter, { justifyContent: "space-between" }]}>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              flex: 1,
              backgroundColor: "transparent",
            }}
          >
            <View style={cardStyles.metaChip}>
              <Ionicons
                name="location-outline"
                size={12}
                color={isDark ? "#E2E8F0" : "#64748B"}
              />
              <Text style={cardStyles.metaChipText}>
                {delivery.State || "-"}
              </Text>
            </View>
            <View style={cardStyles.metaChip}>
              <Ionicons
                name="business-outline"
                size={12}
                color={isDark ? "#E2E8F0" : "#64748B"}
              />
              <Text
                style={cardStyles.metaChipText}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {delivery.Warehouse || "-"}
              </Text>
            </View>
            {delivery.ShippingType ? (
              <View style={cardStyles.metaChip}>
                <Ionicons
                  name="car-outline"
                  size={12}
                  color={isDark ? "#E2E8F0" : "#64748B"}
                />
                <Text
                  style={cardStyles.metaChipText}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {delivery.ShippingType}
                </Text>
              </View>
            ) : null}
            {delivery.SOOperator ? (
              <View style={cardStyles.metaChip}>
                <Ionicons
                  name="person-outline"
                  size={12}
                  color={isDark ? "#E2E8F0" : "#64748B"}
                />
                <Text
                  style={cardStyles.metaChipText}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {delivery.SOOperator}
                </Text>
              </View>
            ) : null}
            {(delivery as any).CustomerPortalOrderNo ? (
              <View style={cardStyles.metaChip}>
                <Ionicons
                  name="receipt-outline"
                  size={12}
                  color={isDark ? "#E2E8F0" : "#64748B"}
                />
                <Text
                  style={cardStyles.metaChipText}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {(delivery as any).CustomerPortalOrderNo}
                </Text>
              </View>
            ) : null}
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "transparent",
            }}
          >
            <TouchableOpacity
              style={{
                padding: 10,
                backgroundColor: isDark ? colors.border : "#F1F5F9",
                borderRadius: 8,
                marginLeft: 8,
                opacity: (delivery as any)?.hasDnAttachment === false ? 0.35 : 1,
              }}
              disabled={(delivery as any)?.hasDnAttachment === false}
              onPress={() => {
                handleDownloadPdf(delivery);
              }}
            >
              <Ionicons
                name="document-text-outline"
                size={20}
                color={
                  (delivery as any)?.hasDnAttachment === false
                    ? "#94A3B8"
                    : isDark
                      ? colors.text
                      : "#3B82F6"
                }
              />
            </TouchableOpacity>

            <TouchableOpacity
              style={{
                padding: 10,
                backgroundColor: isDark ? colors.border : "#F1F5F9",
                borderRadius: 8,
                marginLeft: 8,
              }}
              onPress={() => {
                setSelectedDeliveryItem(delivery);
                setDeliveryAttachmentModalVisible(true);
              }}
            >
              <FeatherIcon
                name="paperclip"
                size={20}
                color={
                  (delivery as any)?.hasAdditionalAttachment
                    ? "#10B981"
                    : isDark
                      ? colors.text
                      : "#334155"
                }
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const MobileDeliveryDetailsModalContent = (props: any) => {
    return (
      <Modal.ModalContent
        {...props}
        title="Delivery Details"
        subTitle={
          selectedDeliveryItem?.CustomerName ||
          selectedDeliveryItem?.DocNum ||
          ""
        }
        icon="folder"
        childern={
          <View style={{ padding: 16, paddingBottom: 32 }}>
            {selectedDeliveryItem?.items?.map((detail: any, idx: number) => (
              <TouchableOpacity
                key={idx}
                style={{
                  marginBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: isDark ? colors.border : "#E2E8F0",
                  paddingBottom: 16,
                }}
                activeOpacity={0.7}
                onPress={() => {
                  setMobileDetailsModalVisible(false);
                  onHandleMobileItemDetails(detail);
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  <View style={{ flex: 1, marginRight: 8 }}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: isDark ? colors.text : "#1E293B",
                      }}
                    >
                      {detail.Dscription || detail.Description || "-"}
                    </Text>
                    <Text
                      style={{
                        fontSize: 12,
                        color: isDark ? colors.textSecondary : "#64748B",
                        marginTop: 4,
                      }}
                    >
                      #{detail.ItemCode}
                    </Text>
                  </View>
                  <View style={{ alignItems: "flex-end" }}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: "bold",
                        color: colors.warning,
                      }}
                    >
                      {Number(detail.OrderQty || 0).toLocaleString("en-US")}{" "}
                      {detail.UomCode || "PCS"}
                    </Text>
                    <View
                      style={{
                        backgroundColor: isDark ? colors.surface : "#F1F5F9",
                        paddingHorizontal: 6,
                        paddingVertical: 2,
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 10,
                          color: isDark ? colors.textSecondary : "#64748B",
                          textTransform: "uppercase",
                          fontWeight: "600",
                        }}
                      >
                        QTY
                      </Text>
                    </View>
                  </View>
                </View>

                {/* Additional Details Grid */}
                <View
                  style={{
                    backgroundColor: isDark ? "#2A2B2E" : "#F8FAFC",
                    padding: 8,
                    borderRadius: 8,
                  }}
                >
                  <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                    {[
                      { label: "Delivered", value: detail.DelivrdQty || "0" },
                      { label: "Remaining", value: detail.RemainingQty || "0" },
                      { label: "Pallets", value: detail.NoOfPallets || "0" },
                      { label: "Product Group", value: detail.ProductGroup || "-" },
                      { label: "Reference", value: detail["CustomerRef."] || "-" },
                      {
                        label: "Address",
                        value: detail.DeliveryAddress?.trim() || "-",
                        fullWidth: true,
                      },
                    ].map((item, i) => (
                      <View
                        key={i}
                        style={{
                          width: item.fullWidth ? "100%" : "33.33%",
                          paddingVertical: 12,
                          paddingHorizontal: 8,
                          borderBottomWidth: i === 5 ? 0 : 1,
                          borderBottomColor: isDark ? "#374151" : "#FFFFFF",
                        }}
                      >
                        <Text
                          selectable={true}
                          style={{
                            fontSize: 10,
                            color: isDark ? "#9CA3AF" : "#94A3B8",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            marginBottom: 4,
                            letterSpacing: 0.5,
                          }}
                        >
                          {item.label}
                        </Text>
                        <View style={{ flexDirection: "row", alignItems: "baseline" }}>
                          <Text
                            selectable={true}
                            style={{
                              fontSize: 13,
                              color: isDark ? "#F9FAFB" : "#334155",
                              fontWeight: "500",
                            }}
                          >
                            {item.value}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              </TouchableOpacity>
            ))}
            {(!selectedDeliveryItem?.items ||
              selectedDeliveryItem.items.length === 0) && (
                <View style={{ alignItems: "center", paddingVertical: 40 }}>
                  <Text style={{ color: isDark ? colors.textSecondary : "#94A3B8" }}>
                    No items found in this order.
                  </Text>
                </View>
              )}
          </View>
        }
      />
    );
  };

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: isDark ? colors.background : "#eef2f6",
      }}
      contentContainerStyle={{
        flexGrow: 1,
        backgroundColor: isDark ? colors.background : "#eef2f6",
      }}
      onScroll={onHandleScroll}
      ref={scrollRef}
    >
      <View
        style={{ backgroundColor: isDark ? colors.background : "#eef2f6" }}
      >
        <View
          style={[
            styles.container,
            {
              backgroundColor: isDark ? colors.background : "#eef2f6",
            },
          ]}
        >
          <View
            style={[
              styles.containerContent,
              {
                width: isLargeDevice
                  ? "100%"
                  : isMediumDevice
                    ? "100%"
                    : "100%",
                backgroundColor: isDark ? colors.background : "#eef2f6",
              },
            ]}
          >
            {!isDeliveryListLoaded && loadingReportV2 ? (
              <View>
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{ flexDirection: "row", margin: "20%" }}
                />
              </View>
            ) : (
              <View
                style={{
                  backgroundColor: isDark ? colors.background : "#eef2f6",
                  flex: 1,
                }}
              >
                {isSmallDevice || isMobileDevice ? (
                  <>
                    <View
                      style={[
                        cardStyles.tableCard,
                        { marginTop: 12, marginBottom: 8 },
                      ]}
                    >
                      <LabelBannerWithSearchBar
                        titleName={`${t("totalDelivery")}: ${totalRows ?? deliveryList.length}`}
                        handleSearch={onHandleSearch}
                        setRefresh={setRefresh}
                        isResetFields={isRefresh}
                        dataToExport={deliveryList}
                        fileName="Delivery_List"
                        sheetName="Delivery"
                        preSearchControls={
                          !(isSmallDevice || isMobileDevice) ? (
                            <RNView>
                              <TouchableOpacity
                                ref={settingsBtnRef}
                                style={{
                                  width:
                                    isSmallDevice || isMobileDevice ? 34 : 40,
                                  height:
                                    isSmallDevice || isMobileDevice ? 34 : 40,
                                  borderRadius:
                                    isSmallDevice || isMobileDevice ? 8 : 10,
                                  backgroundColor: columnSettingsOpen
                                    ? colors.primaryBg
                                    : colors.surfaceSubtle,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderWidth: 1,
                                  borderColor: columnSettingsOpen
                                    ? colors.primary
                                    : colors.border,
                                }}
                                onPress={() => {
                                  if (settingsBtnRef.current) {
                                    settingsBtnRef.current.measureInWindow(
                                      (
                                        x: number,
                                        y: number,
                                        width: number,
                                        height: number,
                                      ) => {
                                        setSettingsBtnPos({
                                          x,
                                          y,
                                          width,
                                          height,
                                        });
                                        setColumnSettingsOpen(
                                          !columnSettingsOpen,
                                        );
                                      },
                                    );
                                  } else {
                                    setColumnSettingsOpen(!columnSettingsOpen);
                                  }
                                }}
                              >
                                <FeatherIcon
                                  name="settings"
                                  size={
                                    isSmallDevice || isMobileDevice ? 14 : 15
                                  }
                                  color={
                                    columnSettingsOpen
                                      ? colors.primary
                                      : colors.textSecondary
                                  }
                                />
                              </TouchableOpacity>
                              <RNModal
                                visible={columnSettingsOpen}
                                transparent={true}
                                animationType="fade"
                                onRequestClose={() =>
                                  setColumnSettingsOpen(false)
                                }
                              >
                                <Pressable
                                  style={{
                                    flex: 1,
                                    backgroundColor: "rgba(0,0,0,0.15)",
                                    ...(isSmallDevice || isMobileDevice
                                      ? {
                                        justifyContent: "center",
                                        alignItems: "center",
                                        padding: 20,
                                      }
                                      : {}),
                                  }}
                                  onPress={() => setColumnSettingsOpen(false)}
                                >
                                  <Pressable
                                    style={{
                                      ...(isSmallDevice || isMobileDevice
                                        ? { width: "100%", maxWidth: 320 }
                                        : {
                                          position: "absolute",
                                          top:
                                            settingsBtnPos.y +
                                            settingsBtnPos.height +
                                            6,
                                          left: settingsBtnPos.x,
                                          minWidth: 220,
                                        }),
                                      backgroundColor: colors.surface,
                                      borderRadius: 12,
                                      borderWidth: 1,
                                      borderColor:
                                        colors.border || colors.borderSubtle,
                                      paddingVertical: 10,
                                      shadowColor: "#000",
                                      shadowOffset: { width: 0, height: 8 },
                                      shadowOpacity: 0.15,
                                      shadowRadius: 20,
                                      elevation: 10,
                                    }}
                                    onPress={(e) => e.stopPropagation()}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        fontWeight: "700",
                                        color:
                                          colors.textSecondary ||
                                          colors.textSecondary,
                                        paddingHorizontal: 16,
                                        paddingBottom: 8,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      {t("showHideColumns")}
                                    </Text>
                                    <View
                                      style={{
                                        height: 1,
                                        backgroundColor: colors.borderSubtle,
                                        marginBottom: 4,
                                      }}
                                    />
                                    {/* Select All */}
                                    <TouchableOpacity
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        paddingVertical: 8,
                                        paddingHorizontal: 16,
                                      }}
                                      onPress={() => {
                                        const allKeys = Object.keys(
                                          getAllHeaders(),
                                        ).filter((k) => k !== "");
                                        if (hiddenColumns.length === 0) {
                                          // Hide all
                                          const updated = [...allKeys];
                                          setHiddenColumns(updated);
                                          AsyncStorage.setItem(
                                            "delivery_hidden_columns",
                                            JSON.stringify(updated),
                                          );
                                        } else {
                                          // Show all
                                          setHiddenColumns([]);
                                          AsyncStorage.setItem(
                                            "delivery_hidden_columns",
                                            JSON.stringify([]),
                                          );
                                        }
                                      }}
                                    >
                                      <View
                                        style={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: 4,
                                          borderWidth: 1.5,
                                          borderColor:
                                            hiddenColumns.length === 0
                                              ? colors.primary
                                              : colors.textMuted,
                                          backgroundColor:
                                            hiddenColumns.length === 0
                                              ? colors.primary
                                              : colors.surface,
                                          justifyContent: "center",
                                          alignItems: "center",
                                          marginRight: 10,
                                        }}
                                      >
                                        {hiddenColumns.length === 0 && (
                                          <FeatherIcon
                                            name="check"
                                            size={13}
                                            color={colors.surface}
                                          />
                                        )}
                                      </View>
                                      <Text
                                        style={{
                                          fontSize: 14,
                                          color: colors.text,
                                          fontWeight: "700",
                                        }}
                                      >
                                        {t("selectAll")}
                                      </Text>
                                    </TouchableOpacity>
                                    <View
                                      style={{
                                        height: 1,
                                        backgroundColor: colors.borderSubtle,
                                        marginBottom: 2,
                                      }}
                                    />
                                    <ScrollView
                                      style={{ maxHeight: 300 }}
                                      showsVerticalScrollIndicator={true}
                                    >
                                      {getOrderedKeys(getAllHeaders()).map(
                                        (key) => {
                                          if (key === "") return null;
                                          const value = getAllHeaders()[key];
                                          if (!value) return null;
                                          const isVisible =
                                            !hiddenColumns.includes(key);
                                          const isDragging =
                                            dragActiveKey === key;
                                          const isDragOver =
                                            dragOverKey === key;
                                          return (
                                            <RNView
                                              key={key}
                                              {...({
                                                dataSet: { columnkey: key },
                                              } as any)}
                                              style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                paddingVertical: 8,
                                                paddingHorizontal: 16,
                                                opacity: isDragging ? 0.35 : 1,
                                                backgroundColor: isDragOver
                                                  ? colors.primaryBg
                                                  : "transparent",
                                                borderTopWidth: isDragOver
                                                  ? 2
                                                  : 0,
                                                borderTopColor: colors.primary,
                                              }}
                                            >
                                              <Pressable
                                                onPressIn={() =>
                                                  startColumnDrag(key)
                                                }
                                                style={
                                                  {
                                                    padding: 4,
                                                    marginRight: 6,
                                                    cursor: "grab",
                                                  } as any
                                                }
                                              >
                                                <FeatherIcon
                                                  name="menu"
                                                  size={14}
                                                  color={colors.textMuted}
                                                />
                                              </Pressable>
                                              <TouchableOpacity
                                                style={{
                                                  flexDirection: "row",
                                                  alignItems: "center",
                                                  flex: 1,
                                                }}
                                                onPress={() =>
                                                  toggleColumn(key)
                                                }
                                              >
                                                <RNView
                                                  style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: 4,
                                                    borderWidth: 1.5,
                                                    borderColor: isVisible
                                                      ? colors.primary
                                                      : colors.borderSubtle,
                                                    backgroundColor: isVisible
                                                      ? colors.primary
                                                      : colors.surface,
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    marginRight: 10,
                                                  }}
                                                >
                                                  {isVisible && (
                                                    <FeatherIcon
                                                      name="check"
                                                      size={13}
                                                      color={colors.surface}
                                                    />
                                                  )}
                                                </RNView>
                                                <Text
                                                  style={{
                                                    fontSize: 14,
                                                    color: isVisible
                                                      ? colors.text
                                                      : colors.textMuted,
                                                    fontWeight: isVisible
                                                      ? "600"
                                                      : "400",
                                                  }}
                                                >
                                                  {t((value as any).name)}
                                                </Text>
                                              </TouchableOpacity>
                                            </RNView>
                                          );
                                        },
                                      )}
                                    </ScrollView>
                                    {columnOrder.length > 0 && (
                                      <>
                                        <RNView
                                          style={{
                                            height: 1,
                                            backgroundColor: "#F1F5F9",
                                            marginTop: 4,
                                          }}
                                        />
                                        <TouchableOpacity
                                          style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            paddingVertical: 10,
                                            paddingHorizontal: 16,
                                          }}
                                          onPress={resetColumnOrder}
                                        >
                                          <FeatherIcon
                                            name="rotate-ccw"
                                            size={13}
                                            color="#6B7280"
                                          />
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              color: "#6B7280",
                                              fontWeight: "600",
                                              marginLeft: 6,
                                            }}
                                          >
                                            {t("resetOrder")}
                                          </Text>
                                        </TouchableOpacity>
                                      </>
                                    )}
                                  </Pressable>
                                </Pressable>
                              </RNModal>
                            </RNView>
                          ) : undefined
                        }
                        middleSearchControls={
                          isSmallDevice || isMobileDevice ? (
                            <TouchableOpacity
                              style={[
                                {
                                  backgroundColor: showMobileFilters
                                    ? colors.primary
                                    : colors.surfaceSubtle,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderWidth: 1,
                                  borderColor: showMobileFilters
                                    ? colors.primary
                                    : colors.border,
                                },
                                (isSmallDevice || isMobileDevice) && {
                                  width: 34,
                                  height: 34,
                                  borderRadius: 8,
                                },
                              ]}
                              onPress={() =>
                                setShowMobileFilters(!showMobileFilters)
                              }
                            >
                              <FeatherIcon
                                name="filter"
                                size={isSmallDevice || isMobileDevice ? 14 : 15}
                                color={
                                  showMobileFilters ? "#FFFFFF" : "#6B7280"
                                }
                              />
                            </TouchableOpacity>
                          ) : undefined
                        }
                        expandableSearchOnMobile={false}
                      />
                    </View>
                    {showMobileFilters && (
                      <View
                        style={{ paddingHorizontal: 10, paddingVertical: 10 }}
                      >
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                        >
                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "date" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "date" ? null : "date",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              {t("date")}:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {selectedDateLabels.length === 0
                                ? "All"
                                : selectedDateLabels.length === 1
                                  ? selectedDateLabels[0] === "Date Range"
                                    ? getDateRangeLabel()
                                    : selectedDateLabels[0]
                                  : `${selectedDateLabels.length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "date" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "state" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                              { marginLeft: 16 },
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "state" ? null : "state",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              {t("state")}:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {(states as string[]).length === 0
                                ? "All"
                                : (states as string[]).length === 1
                                  ? (states as string[])[0]
                                  : `${(states as string[]).length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "state" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "status" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                              { marginLeft: 16 },
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "status" ? null : "status",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              Order Status:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {(statuses as string[]).length === 0
                                ? "All"
                                : (statuses as string[]).length === 1
                                  ? renameLabel(
                                    (
                                      dataToBeConverted as Record<
                                        string,
                                        string
                                      >
                                    )[(statuses as string[])[0]] ||
                                    (statuses as string[])[0],
                                  )
                                  : `${(statuses as string[]).length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "status" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                        </ScrollView>

                        {mobileFilterOpen === "date" && (
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={[
                              deliveryFilterStyles.mobileChipRow,
                              { marginTop: 2 },
                            ]}
                          >
                            {dateFilters
                              .filter((c) => c !== "Date Range")
                              .map((category: string, index: number) => {
                                const isActive =
                                  category === "All"
                                    ? selectedDateLabels.length === 0
                                    : selectedDateLabels.includes(category);
                                return (
                                  <TouchableOpacity
                                    key={`date-${index}`}
                                    style={[
                                      deliveryFilterStyles.filterChip,
                                      isActive &&
                                      deliveryFilterStyles.filterChipActive,
                                    ]}
                                    onPress={() =>
                                      handleDateFilterSelect(category)
                                    }
                                  >
                                    <Text
                                      style={
                                        isActive
                                          ? deliveryFilterStyles.filterChipTextActive
                                          : deliveryFilterStyles.filterChipText
                                      }
                                    >
                                      {category === "Date Range" &&
                                        selectedDateLabels.includes("Date Range")
                                        ? getDateRangeLabel()
                                        : category}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                          </ScrollView>
                        )}

                        {mobileFilterOpen === "state" && (
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={[
                              deliveryFilterStyles.mobileChipRow,
                              { marginTop: 2 },
                            ]}
                          >
                            {stateFilters.map(
                              (category: string, index: number) => {
                                const isActive =
                                  category === "all"
                                    ? (states as string[]).length === 0
                                    : (states as string[]).includes(category);
                                return (
                                  <TouchableOpacity
                                    key={`state-${index}`}
                                    style={[
                                      deliveryFilterStyles.filterChip,
                                      isActive &&
                                      deliveryFilterStyles.filterChipActive,
                                    ]}
                                    onPress={() => handleStateToggle(category)}
                                  >
                                    <Text
                                      style={
                                        isActive
                                          ? deliveryFilterStyles.filterChipTextActive
                                          : deliveryFilterStyles.filterChipText
                                      }
                                    >
                                      {renameLabel(category)}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              },
                            )}
                          </ScrollView>
                        )}

                        {mobileFilterOpen === "status" && (
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={[
                              deliveryFilterStyles.mobileChipRow,
                              { marginTop: 2 },
                            ]}
                          >
                            {["All", ...availableStatuses].map(
                              (status, index) => {
                                const isActive =
                                  status === "All"
                                    ? (statuses as string[]).length === 0
                                    : (statuses as string[]).includes(status);
                                return (
                                  <TouchableOpacity
                                    key={`status-${index}`}
                                    style={[
                                      deliveryFilterStyles.filterChip,
                                      isActive &&
                                      deliveryFilterStyles.filterChipActive,
                                    ]}
                                    onPress={() => handleStatusToggle(status)}
                                  >
                                    <Text
                                      style={
                                        isActive
                                          ? deliveryFilterStyles.filterChipTextActive
                                          : deliveryFilterStyles.filterChipText
                                      }
                                    >
                                      {renameLabel(
                                        (
                                          dataToBeConverted as Record<
                                            string,
                                            string
                                          >
                                        )[status] || status,
                                      )}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              },
                            )}
                          </ScrollView>
                        )}
                      </View>
                    )}
                  </>
                ) : (
                  <>
                    <View style={deliveryFilterStyles.quickFilterBar}>
                      <View style={{ flex: 1 }}>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={
                            deliveryFilterStyles.quickFilterChipsContainer
                          }
                        >
                          <Text style={deliveryFilterStyles.quickFilterLabel}>
                            {t("quickFilter")}
                          </Text>

                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "date" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "date" ? null : "date",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              {t("date")}:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {selectedDateLabels.length === 0
                                ? "All"
                                : selectedDateLabels.length === 1
                                  ? selectedDateLabels[0] === "Date Range"
                                    ? getDateRangeLabel()
                                    : selectedDateLabels[0]
                                  : `${selectedDateLabels.length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "date" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "state" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                              { marginLeft: 8 },
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "state" ? null : "state",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              {t("state")}:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {(states as string[]).length === 0
                                ? "All"
                                : (states as string[]).length === 1
                                  ? (states as string[])[0]
                                  : `${(states as string[]).length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "state" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              deliveryFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "status" &&
                              deliveryFilterStyles.mobileDropdownButtonActive,
                              { marginLeft: 8 },
                            ]}
                            onPress={() =>
                              setMobileFilterOpen(
                                mobileFilterOpen === "status" ? null : "status",
                              )
                            }
                          >
                            <Text
                              style={deliveryFilterStyles.mobileDropdownLabel}
                            >
                              Order Status:{" "}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownValue}
                            >
                              {(statuses as string[]).length === 0
                                ? "All"
                                : (statuses as string[]).length === 1
                                  ? renameLabel(
                                    (
                                      dataToBeConverted as Record<
                                        string,
                                        string
                                      >
                                    )[(statuses as string[])[0]] ||
                                    (statuses as string[])[0],
                                  )
                                  : `${(statuses as string[]).length} ${t("selected")}`}
                            </Text>
                            <Text
                              style={deliveryFilterStyles.mobileDropdownArrow}
                            >
                              {mobileFilterOpen === "status" ? "▲" : "▼"}
                            </Text>
                          </TouchableOpacity>
                        </ScrollView>
                      </View>
                      <View
                        style={deliveryFilterStyles.quickFilterSearchSection}
                      >
                        <LabelBannerWithSearchBar
                          titlePrefix={
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <TouchableOpacity
                                style={{
                                  backgroundColor: colors.surfaceSubtle,
                                  paddingHorizontal: 16,
                                  paddingVertical: 8,
                                  borderRadius: 20,
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                                onPress={() => setRecentUploadsVisible(true)}
                              >
                                <FeatherIcon
                                  name="clock"
                                  size={14}
                                  color={colors.primary}
                                />
                                <Text
                                  style={{
                                    color: colors.text,
                                    fontSize: 13,
                                    fontWeight: "600",
                                  }}
                                >
                                  {t("recentUploads") || "Recent Uploads"}
                                </Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={{
                                  backgroundColor: colors.surfaceSubtle,
                                  paddingHorizontal: 16,
                                  paddingVertical: 8,
                                  borderRadius: 20,
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                                onPress={() => setOrderSearchModalVisible(true)}
                              >
                                <FeatherIcon
                                  name="search"
                                  size={14}
                                  color={colors.primary}
                                />
                                <Text
                                  style={{
                                    color: colors.text,
                                    fontSize: 13,
                                    fontWeight: "600",
                                  }}
                                >
                                  {t("searchArchivedOrders")}
                                </Text>
                              </TouchableOpacity>
                              <Text style={{ color: "#D1D5DB", fontSize: 16 }}>
                                |
                              </Text>
                            </View>
                          }
                          titleName={`${t("totalDelivery")}: ${totalRows ?? deliveryList.length}`}
                          handleSearch={onHandleSearch}
                          setRefresh={setRefresh}
                          isResetFields={isRefresh}
                          dataToExport={deliveryList}
                          fileName="Delivery_List"
                          sheetName="Delivery"
                          preSearchControls={
                            !(isSmallDevice || isMobileDevice) ? (
                              <RNView>
                                <TouchableOpacity
                                  ref={settingsBtnRef}
                                  style={{
                                    width:
                                      isSmallDevice || isMobileDevice ? 34 : 40,
                                    height:
                                      isSmallDevice || isMobileDevice ? 34 : 40,
                                    borderRadius:
                                      isSmallDevice || isMobileDevice ? 8 : 10,
                                    backgroundColor: columnSettingsOpen
                                      ? colors.primaryBg
                                      : colors.surfaceSubtle,
                                    justifyContent: "center",
                                    alignItems: "center",
                                    borderWidth: 1,
                                    borderColor: columnSettingsOpen
                                      ? colors.primary
                                      : colors.border,
                                  }}
                                  onPress={() => {
                                    if (settingsBtnRef.current) {
                                      settingsBtnRef.current.measureInWindow(
                                        (
                                          x: number,
                                          y: number,
                                          width: number,
                                          height: number,
                                        ) => {
                                          setSettingsBtnPos({
                                            x,
                                            y,
                                            width,
                                            height,
                                          });
                                          setColumnSettingsOpen(
                                            !columnSettingsOpen,
                                          );
                                        },
                                      );
                                    } else {
                                      setColumnSettingsOpen(
                                        !columnSettingsOpen,
                                      );
                                    }
                                  }}
                                >
                                  <FeatherIcon
                                    name="settings"
                                    size={
                                      isSmallDevice || isMobileDevice ? 14 : 15
                                    }
                                    color={
                                      columnSettingsOpen
                                        ? colors.primary
                                        : colors.textSecondary
                                    }
                                  />
                                </TouchableOpacity>
                                <RNModal
                                  visible={columnSettingsOpen}
                                  transparent={true}
                                  animationType="fade"
                                  onRequestClose={() =>
                                    setColumnSettingsOpen(false)
                                  }
                                >
                                  <Pressable
                                    style={{
                                      flex: 1,
                                      backgroundColor: "rgba(0,0,0,0.15)",
                                      ...(isSmallDevice || isMobileDevice
                                        ? {
                                          justifyContent: "center",
                                          alignItems: "center",
                                          padding: 20,
                                        }
                                        : {}),
                                    }}
                                    onPress={() => setColumnSettingsOpen(false)}
                                  >
                                    <Pressable
                                      style={{
                                        ...(isSmallDevice || isMobileDevice
                                          ? { width: "100%", maxWidth: 320 }
                                          : {
                                            position: "absolute",
                                            top:
                                              settingsBtnPos.y +
                                              settingsBtnPos.height +
                                              6,
                                            left: settingsBtnPos.x,
                                            minWidth: 220,
                                          }),
                                        backgroundColor: colors.surface,
                                        borderRadius: 12,
                                        borderWidth: 1,
                                        borderColor: colors.borderSubtle,
                                        paddingVertical: 10,
                                        shadowColor: "#000",
                                        shadowOffset: { width: 0, height: 8 },
                                        shadowOpacity: 0.15,
                                        shadowRadius: 20,
                                        elevation: 10,
                                      }}
                                      onPress={(e) => e.stopPropagation()}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          fontWeight: "700",
                                          color: colors.textSecondary,
                                          paddingHorizontal: 16,
                                          paddingBottom: 8,
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {t("showHideColumns")}
                                      </Text>
                                      <View
                                        style={{
                                          height: 1,
                                          backgroundColor: colors.borderSubtle,
                                          marginBottom: 4,
                                        }}
                                      />
                                      {/* Select All */}
                                      <TouchableOpacity
                                        style={{
                                          flexDirection: "row",
                                          alignItems: "center",
                                          paddingVertical: 8,
                                          paddingHorizontal: 16,
                                        }}
                                        onPress={() => {
                                          const allKeys = Object.keys(
                                            getAllHeaders(),
                                          ).filter((k) => k !== "");
                                          if (hiddenColumns.length === 0) {
                                            // Hide all
                                            const updated = [...allKeys];
                                            setHiddenColumns(updated);
                                            AsyncStorage.setItem(
                                              "delivery_hidden_columns",
                                              JSON.stringify(updated),
                                            );
                                          } else {
                                            // Show all
                                            setHiddenColumns([]);
                                            AsyncStorage.setItem(
                                              "delivery_hidden_columns",
                                              JSON.stringify([]),
                                            );
                                          }
                                        }}
                                      >
                                        <View
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 4,
                                            borderWidth: 1.5,
                                            borderColor:
                                              hiddenColumns.length === 0
                                                ? colors.primary
                                                : colors.textMuted,
                                            backgroundColor:
                                              hiddenColumns.length === 0
                                                ? colors.primary
                                                : colors.surface,
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginRight: 10,
                                          }}
                                        >
                                          {hiddenColumns.length === 0 && (
                                            <FeatherIcon
                                              name="check"
                                              size={13}
                                              color={colors.surface}
                                            />
                                          )}
                                        </View>
                                        <Text
                                          style={{
                                            fontSize: 14,
                                            color: colors.text,
                                            fontWeight: "700",
                                          }}
                                        >
                                          {t("selectAll")}
                                        </Text>
                                      </TouchableOpacity>
                                      <View
                                        style={{
                                          height: 1,
                                          backgroundColor: colors.borderSubtle,
                                          marginBottom: 2,
                                        }}
                                      />
                                      <ScrollView
                                        style={{ maxHeight: 300 }}
                                        showsVerticalScrollIndicator={true}
                                      >
                                        {getOrderedKeys(getAllHeaders()).map(
                                          (key) => {
                                            if (key === "") return null;
                                            const value = getAllHeaders()[key];
                                            if (!value) return null;
                                            const isVisible =
                                              !hiddenColumns.includes(key);
                                            const isDragging =
                                              dragActiveKey === key;
                                            const isDragOver =
                                              dragOverKey === key;
                                            return (
                                              <RNView
                                                key={key}
                                                {...({
                                                  dataSet: { columnkey: key },
                                                } as any)}
                                                style={{
                                                  flexDirection: "row",
                                                  alignItems: "center",
                                                  paddingVertical: 8,
                                                  paddingHorizontal: 16,
                                                  opacity: isDragging
                                                    ? 0.35
                                                    : 1,
                                                  backgroundColor: isDragOver
                                                    ? colors.primaryBg
                                                    : "transparent",
                                                  borderTopWidth: isDragOver
                                                    ? 2
                                                    : 0,
                                                  borderTopColor:
                                                    colors.primary,
                                                }}
                                              >
                                                <Pressable
                                                  onPressIn={() =>
                                                    startColumnDrag(key)
                                                  }
                                                  style={
                                                    {
                                                      padding: 4,
                                                      marginRight: 6,
                                                      cursor: "grab",
                                                    } as any
                                                  }
                                                >
                                                  <FeatherIcon
                                                    name="menu"
                                                    size={14}
                                                    color={colors.textMuted}
                                                  />
                                                </Pressable>
                                                <TouchableOpacity
                                                  style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    flex: 1,
                                                  }}
                                                  onPress={() =>
                                                    toggleColumn(key)
                                                  }
                                                >
                                                  <RNView
                                                    style={{
                                                      width: 20,
                                                      height: 20,
                                                      borderRadius: 4,
                                                      borderWidth: 1.5,
                                                      borderColor: isVisible
                                                        ? colors.primary
                                                        : colors.borderSubtle,
                                                      backgroundColor: isVisible
                                                        ? colors.primary
                                                        : colors.surface,
                                                      justifyContent: "center",
                                                      alignItems: "center",
                                                      marginRight: 10,
                                                    }}
                                                  >
                                                    {isVisible && (
                                                      <FeatherIcon
                                                        name="check"
                                                        size={13}
                                                        color={colors.surface}
                                                      />
                                                    )}
                                                  </RNView>
                                                  <Text
                                                    style={{
                                                      fontSize: 14,
                                                      color: isVisible
                                                        ? colors.text
                                                        : colors.textMuted,
                                                      fontWeight: isVisible
                                                        ? "600"
                                                        : "400",
                                                    }}
                                                  >
                                                    {t((value as any).name)}
                                                  </Text>
                                                </TouchableOpacity>
                                              </RNView>
                                            );
                                          },
                                        )}
                                      </ScrollView>
                                      {columnOrder.length > 0 && (
                                        <>
                                          <RNView
                                            style={{
                                              height: 1,
                                              backgroundColor: "#F1F5F9",
                                              marginTop: 4,
                                            }}
                                          />
                                          <TouchableOpacity
                                            style={{
                                              flexDirection: "row",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              paddingVertical: 10,
                                              paddingHorizontal: 16,
                                            }}
                                            onPress={resetColumnOrder}
                                          >
                                            <FeatherIcon
                                              name="rotate-ccw"
                                              size={13}
                                              color="#6B7280"
                                            />
                                            <Text
                                              style={{
                                                fontSize: 13,
                                                color: "#6B7280",
                                                fontWeight: "600",
                                                marginLeft: 6,
                                              }}
                                            >
                                              {t("resetOrder")}
                                            </Text>
                                          </TouchableOpacity>
                                        </>
                                      )}
                                    </Pressable>
                                  </Pressable>
                                </RNModal>
                              </RNView>
                            ) : undefined
                          }
                        />
                      </View>
                    </View>
                    <View style={{ paddingHorizontal: 10, paddingVertical: 0 }}>
                      {mobileFilterOpen === "date" && (
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={
                            deliveryFilterStyles.mobileChipRow
                          }
                        >
                          {dateFilters.map(
                            (category: string, index: number) => {
                              const isActive =
                                category === "All"
                                  ? selectedDateLabels.length === 0
                                  : selectedDateLabels.includes(category);
                              return (
                                <TouchableOpacity
                                  key={`date-${index}`}
                                  style={[
                                    deliveryFilterStyles.filterChip,
                                    isActive &&
                                    deliveryFilterStyles.filterChipActive,
                                  ]}
                                  onPress={() =>
                                    handleDateFilterSelect(category)
                                  }
                                >
                                  <Text
                                    style={
                                      isActive
                                        ? deliveryFilterStyles.filterChipTextActive
                                        : deliveryFilterStyles.filterChipText
                                    }
                                  >
                                    {category === "Date Range" &&
                                      selectedDateLabels.includes("Date Range")
                                      ? getDateRangeLabel()
                                      : category}
                                  </Text>
                                </TouchableOpacity>
                              );
                            },
                          )}
                        </ScrollView>
                      )}

                      {mobileFilterOpen === "state" && (
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={
                            deliveryFilterStyles.mobileChipRow
                          }
                        >
                          {stateFilters.map(
                            (category: string, index: number) => {
                              const isActive =
                                category === "all"
                                  ? (states as string[]).length === 0
                                  : (states as string[]).includes(category);
                              return (
                                <TouchableOpacity
                                  key={`state-${index}`}
                                  style={[
                                    deliveryFilterStyles.filterChip,
                                    isActive &&
                                    deliveryFilterStyles.filterChipActive,
                                  ]}
                                  onPress={() => handleStateToggle(category)}
                                >
                                  <Text
                                    style={
                                      isActive
                                        ? deliveryFilterStyles.filterChipTextActive
                                        : deliveryFilterStyles.filterChipText
                                    }
                                  >
                                    {renameLabel(category)}
                                  </Text>
                                </TouchableOpacity>
                              );
                            },
                          )}
                        </ScrollView>
                      )}

                      {mobileFilterOpen === "status" && (
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={{
                            paddingTop: 8,
                            paddingBottom: 4,
                          }}
                        >
                          {["All", ...availableStatuses].map(
                            (status, index) => {
                              const isActive =
                                status === "All"
                                  ? (statuses as string[]).length === 0
                                  : (statuses as string[]).includes(status);
                              return (
                                <TouchableOpacity
                                  key={`status-${index}`}
                                  style={[
                                    deliveryFilterStyles.filterChip,
                                    isActive &&
                                    deliveryFilterStyles.filterChipActive,
                                  ]}
                                  onPress={() => handleStatusToggle(status)}
                                >
                                  <Text
                                    style={
                                      isActive
                                        ? deliveryFilterStyles.filterChipTextActive
                                        : deliveryFilterStyles.filterChipText
                                    }
                                  >
                                    {renameLabel(
                                      (
                                        dataToBeConverted as Record<
                                          string,
                                          string
                                        >
                                      )[status] || status,
                                    )}
                                  </Text>
                                </TouchableOpacity>
                              );
                            },
                          )}
                        </ScrollView>
                      )}
                    </View>
                  </>
                )}
                {isSmallDevice || isMobileDevice ? (
                  <View style={cardStyles.cardList}>
                    {loadingReportV2 ? (
                      <View
                        style={{
                          padding: 40,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Spinner size="small" color="#74BBFF" />
                      </View>
                    ) : groupedDeliveryList.length === 0 ? (
                      <View style={cardStyles.emptyState}>
                        <Ionicons
                          name="document-text-outline"
                          size={48}
                          color="#CBD5E1"
                        />
                        <Text style={cardStyles.emptyStateText}>
                          {t("noDataAvailable") || "No Data Available"}
                        </Text>
                      </View>
                    ) : (
                      <>
                        {(
                          replaceDataValues(
                            groupedDeliveryList,
                            dataToBeConverted,
                          ) as deliveryModel[]
                        ).map((delivery: deliveryModel, index: number) => (
                          <MobileDeliveryCard
                            key={delivery.DocNum || index}
                            delivery={delivery}
                          />
                        ))}
                        <ThemeProvider
                          theme={{
                            ...paperTheme,
                            dark: isDark,
                            colors: {
                              ...paperTheme.colors,
                              text: isDark ? colors.text : "#0F172A",
                              onSurface: isDark
                                ? colors.textSecondary
                                : "#0F172A",
                            },
                          }}
                        >
                          <DataTable.Pagination
                            page={page - 1}
                            numberOfPages={Math.max(
                              1,
                              Math.ceil((totalRows || 0) / (pageSize || 1)),
                            )}
                            onPageChange={(newPage) =>
                              dispatch(updateReportV2Page(newPage + 1))
                            }
                            label={`${totalRows === 0 ? 0 : (page - 1) * pageSize + 1}-${Math.min(
                              page * pageSize,
                              totalRows || 0,
                            )} of ${totalRows || 0}`}
                            showFastPaginationControls
                          />
                        </ThemeProvider>
                      </>
                    )}
                  </View>
                ) : (
                  <ServerSidePagination
                    selectable={true}
                    headers={tableHeaders}
                    rows={replaceDataValues(
                      groupedDeliveryList,
                      dataToBeConverted,
                    )}
                    itemsPerPage={pageSize}
                    isDataLoading={loadingReportV2}
                    useModal={true}
                    nestedHeader={nestedTableHeaders}
                    isRowsExpandable={true}
                    setModalVisible={setDeliveryAttachmentModalVisible}
                    handleRowClick={onHandleRowClick}
                    setSelected={setSelectedDeliveryItem}
                    setSelectedDeliveryItem={setSelectedDeliveryItem}
                    isToExpandAllRows={_isDisplayViewOn}
                    rowKeyValue="DocNum"
                    nestedItemKeyValue="items"
                    isSubDetail={true}
                    isDetail={true}
                    isEdit={false}
                    isDelete={false}
                    columnsToNotHaveThousandSeparator={
                      columnsToNotHaveThousandsSeparator
                    }
                    isDeliveryAttachment={true}
                    showAttachmentIcon={false}
                    hideInfoIcon={false}
                    setDeliveryModalVisible={setDeliveryAttachmentModalVisible}
                    setDeliveryStatusModal={setDeliveryStatusModal}
                    handleSortColumn={onHandleSortColumn}
                    handleDeleteRow={function (id: string | number): void {
                      throw new Error("Function not implemented.");
                    }}
                    operationActions={navigateToReportDetails}
                    handleDownloadPdf={handleDownloadPdf}
                    serverSidePagination={true}
                    totalCount={totalRows}
                    page={page}
                    onPageChange={(newPage: number) =>
                      dispatch(updateReportV2Page(newPage))
                    }
                  />
                )}
              </View>
            )}
          </View>
        </View>
        <OrderNumberSearchModal
          visible={orderSearchModalVisible}
          onClose={() => setOrderSearchModalVisible(false)}
          onDownloadPdf={handleDownloadPdf}
          onRowClick={onHandleRowClick}
          setSelected={setSelectedDeliveryItem}
          setDeliveryModalVisible={setDeliveryAttachmentModalVisible}
          setDeliveryStatusModal={setDeliveryStatusModal}
        />

        <DownloadPdfDeliveryModal
          downloadPdfModalVisible={downloadPdfModalVisible}
          setDownloadPdfModalVisible={setDownloadPdfModalVisible}
          selectedItem={selectedDeliveryItem}
        />

        <DeliveryRecentUploadsModal
          visible={recentUploadsVisible}
          onClose={() => setRecentUploadsVisible(false)}
          deliveryList={deliveryList}
          onRowClick={(row) => {
            const item = deliveryList.find(
              (i: any) => i.DocNum === row.docNum,
            ) || { DocNum: row.docNum };
            setSelectedDeliveryItem(item);
            setDeliveryAttachmentModalVisible(true);
          }}
        />

        <UploadAttachmentDeliveryModal
          deliveryAttachmentModalVisible={deliveryAttachmentModalVisible}
          setDeliveryAttachmentModalVisible={setDeliveryAttachmentModalVisible}
          selectedItem={selectedDeliveryItem}
        />

        {deliveryStatusModal && (
          <DeliveryStatusConfirmModal
            modalVisible={deliveryStatusModal}
            setModalVisible={setDeliveryStatusModal}
            selectedItem={selectedDeliveryItem}
          />
        )}

        {/* @ts-ignore: DatePickerModal typing conflict */}
        <PaperProvider
          theme={{
            ...DefaultTheme,
            colors: {
              ...DefaultTheme.colors,
              primary: "#3B82F6",
              primaryContainer: "#DBEAFE",
              onPrimaryContainer: "#1E40AF",
              secondaryContainer: "#BFDBFE",
              onSecondaryContainer: "#1E3A5F",
              onSurface: "#1E293B",
              onSurfaceVariant: "#475569",
            },
          }}
        >
          <DatePickerModal
            locale="en-AU"
            mode="range"
            visible={dateRangePickerOpen}
            onDismiss={() => setDateRangePickerOpen(false)}
            startDate={customDateRange.startDate}
            endDate={customDateRange.endDate}
            onConfirm={({ startDate, endDate }: any) => {
              setDateRangePickerOpen(false);
              if (startDate) {
                const finalEndDate = endDate || startDate;
                // Normalize times to midnight
                startDate.setHours(0, 0, 0, 0);
                finalEndDate.setHours(0, 0, 0, 0);
                setCustomDateRange({ startDate, endDate: finalEndDate });
                setSelectedDateLabels(["Date Range"]);
                dispatch(updateReportV2OnlyToday(false));
                dispatch(updateReportV2FetchAll(false));
                dispatch(
                  updateReportV2DateRange({
                    dateFrom: dayjs(startDate).format("YYYY-MM-DD"),
                    dateTo: dayjs(finalEndDate).format("YYYY-MM-DD"),
                  }),
                );
                dispatch(updateReportV2Page(1));
              }
            }}
          />
        </PaperProvider>

        <Modal.ModalContainer
          modalVisible={mobileDetailsModalVisible}
          setModalVisible={setMobileDetailsModalVisible}
          childern={MobileDeliveryDetailsModalContent}
        />

        {/* Item Details Pop Up */}
        {itemDetailsModalVisible && (
          <DeliveryDetailsScreen
            order={selectedItemDetail}
            onBackPress={() => setItemDetailsModalVisible(false)}
          />
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
});

const createDeliveryFilterStyles = (colors: any) =>
  StyleSheet.create({
    quickFilterBar: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      paddingTop: 8,
      paddingBottom: 4,
      paddingHorizontal: 10,
    },
    quickFilterChipsContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingRight: 12,
    },
    quickFilterLabel: {
      fontWeight: "700",
      fontSize: 13,
      color: colors.text,
      marginRight: 10,
    },
    quickFilterSearchSection: {
      flexShrink: 0,
      minWidth: 350,
    },
    mobileDropdownButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      paddingHorizontal: 12,
      backgroundColor: colors.surfaceSubtle || "#F1F5F9",
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border || "#E2E8F0",
    },
    mobileDropdownButtonActive: {
      backgroundColor: colors.primary + "15",
      borderColor: colors.primary + "40",
    },
    mobileDropdownLabel: {
      fontSize: 13,
      fontWeight: "500",
      color: colors.textSecondary || "#64748B",
    },
    mobileDropdownValue: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.primary || "#3B82F6",
      marginRight: 4,
    },
    mobileDropdownArrow: {
      fontSize: 10,
      color: colors.primary || "#3B82F6",
      marginLeft: 4,
    },
    mobileChipRow: {
      flexDirection: "row",
      paddingVertical: 2,
      paddingHorizontal: 4,
    },
    filterChip: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle || "#F1F5F9",
      borderRadius: 999,
      marginRight: 12,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: colors.border || "#E2E8F0",
    },
    filterChipActive: {
      backgroundColor: colors.primary || "#61B8FF",
      borderColor: colors.primary || "#61B8FF",
    },
    filterChipText: {
      fontSize: 13,
      color: colors.textSecondary || "#475569",
      fontWeight: "500" as const,
    },
    filterChipTextActive: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600" as const,
    },
  });

function createCardStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    cardList: {
      paddingHorizontal: 16,
      paddingBottom: 24,
      paddingTop: 8,
      gap: 12,
      backgroundColor: "transparent",
    },
    tableCard: {
      marginHorizontal: 16,
      marginBottom: 24,
      marginTop: 12,
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      overflow: "hidden" as any,
      shadowColor: isDark ? colors.text : "#0F172A",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.07,
      shadowRadius: 12,
      elevation: 4,
    },
    card: {
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E8EDF5",
      shadowColor: isDark ? colors.text : "#1E293B",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.07,
      shadowRadius: 10,
      elevation: 3,
      overflow: "hidden" as any,
      marginBottom: 4,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
    },
    cardHeaderLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: "transparent",
      flex: 1,
      paddingRight: 10,
    },
    containerIconWrap: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
      alignItems: "center",
      justifyContent: "center",
    },
    containerNumber: {
      fontSize: 14,
      fontWeight: "700" as const,
      color: isDark ? colors.text : "#0F172A",
      letterSpacing: 0.2,
      flexWrap: "wrap",
      backgroundColor: "transparent",
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 5,
      paddingHorizontal: 10,
      borderRadius: 999,
      backgroundColor: isDark ? colors.surfaceSubtle : "#EFF6FF",
      gap: 5,
    },
    statusDot: {
      width: 7,
      height: 7,
      borderRadius: 999,
    },
    statusText: {
      fontSize: 12,
      fontWeight: "600" as const,
    },
    divider: {
      height: 1,
      backgroundColor: isDark ? colors.background : "#f1f5f9",
      marginHorizontal: 16,
    },
    dateRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      gap: 4,
    },
    dateBlock: {
      flex: 1,
      alignItems: "center",
      gap: 3,
    },
    dateLabel: {
      fontSize: 10,
      color: isDark ? "#9CA3AF" : "#94A3B8",
      fontWeight: "600" as const,
      textTransform: "uppercase" as any,
      letterSpacing: 0.5,
    },
    dateValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#1E293B",
      fontWeight: "700" as const,
    },
    cardFooter: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap" as any,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 1,
      borderTopColor: isDark ? colors.background : "#f1f5f9",
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
      gap: 8,
    },
    metaChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 8,
      gap: 4,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      flexShrink: 1,
      maxWidth: "100%",
    },
    metaChipText: {
      fontSize: 11,
      color: isDark ? "#E2E8F0" : "#6B7280",
      fontWeight: "500" as const,
      flexShrink: 1,
    },
    emptyState: {
      alignItems: "center",
      paddingVertical: 60,
      gap: 12,
    },
    emptyStateText: {
      fontSize: 15,
      color: "#CBD5E1",
      fontWeight: "500" as const,
    },
  });
}
